import React, { useState, useEffect } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { GET_RECENT_EVENTS, EVENT_CREATED_SUBSCRIPTION, GET_SYSTEM_SETTINGS } from '../../graphql/queries';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import { Activity, User, Clock, Maximize2, X, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

interface Event {
  id: string;
  timestamp: string;
  note?: string;
  duration?: number;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
  };
  eventType: {
    id: string;
    name: string;
    code: string;
    color: string;
  };
}

interface RealtimeEventsFeedProps {
  maxEvents?: number;
}

export const RealtimeEventsFeed: React.FC<RealtimeEventsFeedProps> = ({ maxEvents = 10 }) => {
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [newEventId, setNewEventId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [enableSubscription, setEnableSubscription] = useState(false);

  // Load initial events - only fetch what we need!
  const { data: initialData, loading } = useQuery<{ recentEvents: Event[] }>(
    GET_RECENT_EVENTS,
    {
      variables: { limit: maxEvents },
      fetchPolicy: 'network-only',
    }
  );

  // Load system settings for duration limits
  const { data: settingsData } = useQuery<{
    systemSettings: {
      workDurationLimitMinutes: number;
      breakDurationLimitMinutes: number;
      smokeDurationLimitMinutes: number;
    };
  }>(GET_SYSTEM_SETTINGS, {
    fetchPolicy: 'cache-first',
  });

  // Delay subscription activation to give backend time to be ready
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableSubscription(true);
    }, 2000); // Wait 2 seconds before activating subscription

    return () => clearTimeout(timer);
  }, []);

  // Subscribe to new events
  const { data: subscriptionData, loading: subLoading, error: subError } = useSubscription<{ eventCreated: Event }>(
    EVENT_CREATED_SUBSCRIPTION,
    {
      skip: !enableSubscription, // Skip subscription until enabled
      onData: ({ data }) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔔 New event received via subscription:', data?.data?.eventCreated);
        }
      },
      onError: (error) => {
        // Silently handle errors - auto-retry will handle reconnection
        if (process.env.NODE_ENV === 'development') {
          console.debug('Subscription error (will retry):', error.message);
        }
      },
      onComplete: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Subscription completed');
        }
      },
    }
  );

  // Log subscription status (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📡 Subscription status:', {
        enabled: enableSubscription,
        loading: subLoading,
        hasData: !!subscriptionData,
        hasError: !!subError,
        error: subError?.message,
      });
    }
  }, [enableSubscription, subLoading, subscriptionData, subError]);

  // Initialize with existing events
  useEffect(() => {
    if (initialData?.recentEvents) {
      setRecentEvents(initialData.recentEvents);
    }
  }, [initialData]);

  // Handle new events from subscription
  useEffect(() => {
    if (subscriptionData?.eventCreated) {
      const newEvent = subscriptionData.eventCreated;

      setRecentEvents((prev) => {
        // Check if event already exists (prevent duplicates)
        if (prev.some(e => e.id === newEvent.id)) {
          return prev;
        }

        // Add new event at the top and keep only maxEvents
        return [newEvent, ...prev].slice(0, maxEvents);
      });

      // Set the new event ID for animation
      setNewEventId(newEvent.id);

      // Remove highlight after animation
      setTimeout(() => {
        setNewEventId(null);
      }, 2000);
    }
  }, [subscriptionData, maxEvents]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'HH:mm:ss', { locale: pl });
    } catch {
      return timestamp;
    }
  };

  const formatDate = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'dd.MM.yyyy', { locale: pl });
    } catch {
      return timestamp;
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const isDurationExceeded = (event: Event): boolean => {
    if (!event.duration || !settingsData?.systemSettings) return false;

    const settings = settingsData.systemSettings;
    const eventCode = event.eventType.code;

    switch (eventCode) {
      case 'WORK_END':
        return event.duration > settings.workDurationLimitMinutes;
      case 'BREAK_END':
        return event.duration > settings.breakDurationLimitMinutes;
      case 'SMOKE_END':
        return event.duration > settings.smokeDurationLimitMinutes;
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Wydarzenia na żywo
          </CardTitle>
          <CardDescription>
            Ostatnie wydarzenia w systemie
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const EventsContent = () => (
    <>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className={`${isFullscreen ? 'h-8 w-8' : 'h-5 w-5'} text-primary animate-pulse`} />
            <div>
              <CardTitle className={isFullscreen ? 'text-3xl' : ''}>Wydarzenia na żywo</CardTitle>
              <CardDescription className={isFullscreen ? 'text-lg' : ''}>
                Ostatnie wydarzenia w systemie (aktualizowane na bieżąco)
              </CardDescription>
            </div>
          </div>
          <Tooltip
            content={isFullscreen ? "Zamknij pełny ekran (ESC)" : "Pełny ekran"}
            side="bottom"
          >
            <Button
              variant="ghost"
              size={isFullscreen ? "default" : "sm"}
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-2"
            >
              {isFullscreen ? (
                <>
                  <X className="h-5 w-5" />
                  <span>Zamknij</span>
                </>
              ) : (
                <>
                  <Maximize2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Pełny ekran</span>
                </>
              )}
            </Button>
          </Tooltip>
        </div>
      </CardHeader>
      <CardContent>
        <div className={`space-y-2 ${isFullscreen ? 'max-h-[80vh]' : 'max-h-[400px]'} overflow-y-auto`}>
          {recentEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Brak wydarzeń do wyświetlenia</p>
            </div>
          ) : (
            recentEvents.map((event) => {
              const isExceeded = isDurationExceeded(event);
              return (
              <div
                key={event.id}
                className={`
                  p-3 rounded-lg border transition-all duration-500
                  ${isExceeded ? 'border-l-4 border-l-red-600 dark:border-l-red-500' : ''}
                  ${newEventId === event.id
                    ? 'bg-primary/10 border-primary shadow-lg ring-2 ring-primary/30'
                    : 'bg-card hover:bg-accent/50'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium truncate">
                        {event.employee.lastName} {event.employee.firstName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${event.eventType.color}20`,
                          color: event.eventType.color,
                        }}
                      >
                        {event.eventType.name}
                      </span>
                    </div>
                    {event.note && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        {event.note}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end text-xs text-muted-foreground flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-mono">{formatTimestamp(event.timestamp)}</span>
                    </div>
                    {event.duration !== null && event.duration !== undefined && (
                      <div className={`flex items-center gap-1 font-semibold ${isExceeded ? 'text-red-600 dark:text-red-500' : 'text-primary'}`}>
                        {isExceeded && <AlertTriangle className="h-3 w-3" />}
                        <span>{formatDuration(event.duration)}</span>
                      </div>
                    )}
                    <span className="font-mono">{formatDate(event.timestamp)}</span>
                  </div>
                </div>
              </div>
            );
            })
          )}
        </div>
      </CardContent>
    </>
  );

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container h-full py-8">
          <Card className="h-full flex flex-col">
            <EventsContent />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Card className="h-full">
      <EventsContent />
    </Card>
  );
};
